<script lang="ts">
  import { createEventDispatcher, onMount } from 'svelte';
  import { fly } from 'svelte/transition';
  import { Button, Title, Text, Image } from '@lib/components';
  import { slide } from '@lib/directives';
  import type { CallToAction } from '@lib/types/survey';
  import type { QuestionHeaderProps } from '@lib/components/survey/question/QuestionHeader/types';
  import type { LandingPageConfig } from './types';
  import type { ElementBlock } from '@lib/components/elements';
  import ScreenBlock from '../Screen/ScreenBlock.svelte';
  import { browser } from '$app/environment';

  export let header: QuestionHeaderProps | undefined = undefined;
  export let cta: CallToAction | undefined = undefined;
  export let config: LandingPageConfig | undefined = undefined;
  export let blocks: ElementBlock[] | undefined = undefined;
  export let shouldDismantle = false;

  const dispatch = createEventDispatcher<{ click: void; autoSubmit: void; getGeolocation: void; requestFullscreen: void }>();

  // Get normalized scores from sessionStorage for condition evaluation
  function getNormalizedScores(): Record<string, number> {
    if (!browser) return {};
    try {
      const raw = sessionStorage.getItem('walku:weights');
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed?.normalizedScores || {};
    } catch {
      return {};
    }
  }

  // Get references from sessionStorage for condition evaluation
  function getReferences(): Record<string, any> {
    if (!browser) return {};
    try {
      const raw = sessionStorage.getItem('walku:profileReferences');
      if (!raw) return {};
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }

  // Evaluate block conditions (axis-based and reference-based)
  function doesBlockPassConditions(block: ElementBlock, scores: Record<string, number>, references: Record<string, any>): boolean {
    // Check axis conditions
    const conditions = (block as any).conditions;
    if (conditions && conditions.length > 0) {
      const hasAnyScore = scores && Object.keys(scores).length > 0;
      const allInverted = conditions.every((cond: any) => cond.invert === true);

      // If all conditions are inverted and no scores exist, show block
      if (allInverted && !hasAnyScore) return true;
      // If no scores exist and conditions aren't all inverted, hide block
      if (!hasAnyScore) return false;

      // Evaluate each axis condition
      const axisConditionsMet = conditions.every((cond: { axis: string; min?: number; max?: number; invert?: boolean }) => {
        if (!(cond.axis in scores)) return false;
        const score = scores[cond.axis] as number;
        let passes = true;
        if (cond.min !== undefined && score < cond.min) passes = false;
        if (cond.max !== undefined && score > cond.max) passes = false;
        return cond.invert ? !passes : passes;
      });

      if (!axisConditionsMet) return false;
    }

    // Check reference conditions
    const referenceConditions = (block as any).referenceConditions;
    if (referenceConditions && referenceConditions.length > 0) {
      const refConditionsMet = referenceConditions.every((cond: { field: string; value?: any; values?: any[] }) => {
        // Parse field path (e.g., 'references.actividad_comunitaria')
        if (cond.field.startsWith('references.')) {
          const refKey = cond.field.replace('references.', '');
          const userValue = references[refKey];

          // Check single value match
          if (cond.value !== undefined) {
            return userValue === cond.value;
          }

          // Check multiple values match (OR)
          if (cond.values !== undefined) {
            return cond.values.includes(userValue);
          }

          return true; // No value constraint
        }

        // Future: support other prefixes (metadata., responses., etc.)
        return false; // Unknown prefix
      });

      if (!refConditionsMet) return false;
    }

    return true; // All conditions met (or no conditions)
  }

  $: normalizedScores = getNormalizedScores();
  $: references = getReferences();
  $: filteredBlocks = blocks?.filter(block => doesBlockPassConditions(block, normalizedScores, references)) || [];
  
  function handleCtaAction(action?: string) {
    if (action === 'startSurvey') {
      // For startSurvey, dispatch 'click' which goes through the normal navigation pipeline
      dispatch('click');
    } else if (action === 'submit') {
      dispatch('autoSubmit');
    } else if (action === 'getGeolocation') {
      dispatch('getGeolocation');
    } else if (action === 'backBrowser') {
      window.history.back();
    }
  }
  
  // Check if first block wants full width (legacy or new format)
  $: firstBlockFullWidth = filteredBlocks?.[0] && (filteredBlocks[0].fullWidth || filteredBlocks[0].layout?.width === 'full');
  
  // Handle dismantling when shouldDismantle changes
  $: if (shouldDismantle) {
    handleDismantle();
  }
  
  let visible = true;
  let showContent = true;
  let backgroundEl: HTMLElement;

  onMount(() => {
    // Auto-submit if configured
    if (config?.actions?.autoSubmit) {
      dispatch("autoSubmit");
    }

    // Get geolocation if configured to run on page load
    if (config?.actions?.getGoogleGeolocation?.onPageLoad) {
      dispatch("getGeolocation");
    }

    // Request fullscreen if configured
    if (config?.fullscreen) {
      dispatch("requestFullscreen");
    }

    // Handle onload redirect if configured
    if (config?.actions?.onload?.action === 'redirectTo') {
      const delay = config.actions.onload.delay || 0;
      const destination = config.actions.onload.destination;
      if (destination) {
        setTimeout(() => {
          window.location.href = destination;
        }, delay);
      }
    }

    // Add fade-in class after a frame to trigger transition
    requestAnimationFrame(() => {
      if (backgroundEl) {
        backgroundEl.classList.add('svelte-fade-in');
      }
    });
  });

  async function handleDismantle() {
    if (!shouldDismantle) return;
    
    // Start fading out background
    if (backgroundEl) {
      backgroundEl.classList.remove('svelte-fade-in');
      backgroundEl.classList.add('svelte-fade-out');
    }

    await new Promise((resolve) => setTimeout(resolve, 250));
    // Start content fade - this triggers out:fly on all elements
    showContent = false;

    // Wait for content out:fly transitions to complete
    await new Promise((resolve) => setTimeout(resolve, 300));

    // Finally set visible to false to remove from DOM
    visible = false;
  }

  async function handleClick() {
    // Check if we need to get geolocation data when button is clicked
    if (config?.actions?.getGoogleGeolocation && !config?.actions?.getGoogleGeolocation?.onPageLoad) {
      dispatch("getGeolocation");
    }
    
    dispatch('click');
  }
</script>

<div class="landing-page-component" class:scrollable={config?.scrollable} class:fullscreen={config?.fullscreen} class:allow-text-selection={config?.allowTextSelection} class:first-fullwidth={firstBlockFullWidth}>
  {#if visible}
    <div
      class="background-wrapper"
      bind:this={backgroundEl}
      style:background-image={config?.background?.type === 'image' && config.background.image
        ? `url(${config.background.image.url})`
        : undefined}
      style:--bg-color={config?.background?.type === 'color' ? config.background.value : undefined}
      style:--dark-bg-color={config?.background?.type === 'color' && config.background.darkValue
        ? config.background.darkValue
        : undefined}
    >
      {#if config?.background?.type === 'image' && config.background.overlay}
        <div
          class="overlay"
          style:--overlay-color={config.background.overlay.color}
          style:--overlay-dark-color={config.background.overlay.darkColor}
          style:opacity={config.background.overlay.opacity}
        ></div>
      {/if}
    </div>
  {/if}

  {#if showContent}
    <!-- Render sticky banners first, outside the content wrapper -->
    {#if filteredBlocks && filteredBlocks.length > 0}
      {#each filteredBlocks as block, index}
        {#if block.type === 'banner' && block.sticky}
          <ScreenBlock {block} {index} isFullscreen={config?.fullscreen} onCtaClick={handleCtaAction} />
        {/if}
      {/each}
    {/if}
    
    <div 
      class="content"
      style:--screen-text-color={config?.color}
      out:fly={{ x: '-4%', duration: 200, delay: 0 }}
    >

      {#if filteredBlocks && filteredBlocks.length > 0}
        <div
          class="blocks-container"
          class:has-fullwidth={filteredBlocks.some(b => b.fullWidth || b.layout?.width === 'full' || (b.layout?.width && typeof b.layout.width === 'number' && b.layout.width > 800))}
          out:fly={{ x: '-3%', duration: 200, delay: 10 }}
        >
          {#each filteredBlocks as block, index}
            {#if !(block.type === 'banner' && block.sticky)}
              <div
                class="block-wrapper"
                class:full-width={block.fullWidth || block.type === 'hero'}
                class:first-block={index === 0}
                use:slide={config?.fullscreen ? { translate: 0, duration: 0, delay: 0, startOpacity: 1 } : { translate: 5, duration: 400, delay: 600 + (index * 120), startOpacity: 0 }}
                out:fly={{ x: '-3%', duration: 200, delay: index * 25 }}
              >
                <ScreenBlock {block} {index} isFullscreen={config?.fullscreen} onCtaClick={handleCtaAction} />
              </div>
            {/if}
          {/each}
        </div>
      {/if}

      {#if cta}
        <div
          use:slide={{ translate: 2, duration: 400, delay: 800, startOpacity: 0 }}
          out:fly={{ x: '-3%', duration: 200, delay: 150 }}
        >
          <Button label={cta.label} variant={cta.variant} size="md" on:click={handleClick} />
        </div>
      {/if}
    </div>
  {/if}
</div>

<style lang="postcss">
  .landing-page-component {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    justify-content: center;
    container-type: inline-size;
    box-sizing: border-box;
    position: relative;
    isolation: isolate;
    overflow-x: hidden;
  }
  

  /* Only disable text selection when NOT allowing it */
  .landing-page-component:not(.allow-text-selection) {
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }

  .landing-page-component.scrollable {
    overflow-y: auto;
    justify-content: flex-start;
    align-items: center;
  }

  .background-wrapper {
    position: absolute;
    inset: 0;
    z-index: 1;
    background-position: center bottom;
    background-size: cover;
    background-repeat: no-repeat;
    opacity: 0;
    transition: opacity 800ms ease-in-out;
  }

  .background-wrapper:global(.svelte-fade-in) {
    opacity: 1;
  }

  .background-wrapper:global(.svelte-fade-out) {
    opacity: 0;
    transition: opacity 450ms ease-in-out;
  }

  .overlay {
    position: absolute;
    inset: 0;
    z-index: 1;
    background-color: var(--overlay-color);
  }

  /* Dark mode support */
  :global([data-theme='dark']) .background-wrapper {
    background-color: var(--dark-bg-color, var(--bg-color-dark, #1a1a1a));
  }

  :global([data-theme='dark']) .overlay {
    background-color: var(--overlay-dark-color, var(--overlay-color));
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin: 0 auto;
    width: 100%;
    flex-shrink: 0;
    flex: 1;
    justify-content: center;
    position: relative;
    z-index: 2;
    --text-color: var(--screen-text-color, var(--theme-text-color));
    --spacing-x: 1rem;
    box-sizing: border-box;
  }

  .image-container {
    width: 100%;
    overflow: hidden;
    margin: 0 auto;
    flex-shrink: 0;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  /* Content width constraints - not for fullscreen mode */
  @container (min-width: 600px) {
    .landing-page-component:not(.fullscreen) .image-container,
    .landing-page-component:not(.fullscreen) .content {
      max-width: 600px;
    }
  }

  .text-content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .title-wrapper {
    isolation: isolate;
  }

  @container (max-width: 280px) {
    .landing-page-component {
      gap: 1rem;
    }

    .content {
      gap: 1rem;
    }

    .text-content {
      gap: 0.75rem;
    }

    :global(.title-container h2) {
      font-size: 1rem;
    }
  }

  .hidden {
    display: none;
  }
  .image-inner {
    width: 100%;
  }

  .blocks-container {
    width: 100%;
    max-width: 600px;
  }
  
  /* When container has fullwidth blocks, allow it to expand */
  .blocks-container.has-fullwidth {
    max-width: none;
  }
  
  .block-wrapper {
    width: 100%;
  }
  
  /* Full-width blocks need their wrapper to extend too */
  .block-wrapper.full-width {
    margin-left: calc(-1 * var(--spacing-x, 1rem));
    margin-right: calc(-1 * var(--spacing-x, 1rem));
    width: calc(100% + 2 * var(--spacing-x, 1rem));
  }

  /* Remove border radius for full-width blocks */
  .block-wrapper.full-width .hero-block,
  .block-wrapper.full-width .banner-block {
    border-radius: 0 !important;
  }
  
  /* First block with full width also removes top margin */
  .block-wrapper.full-width.first-block {
    margin-top: calc(-1 * var(--spacing-x, 1rem));
  }


  /* Fullscreen mode - Substack-like layout */
  .landing-page-component.fullscreen .content {
    max-width: none;
  }

  .landing-page-component.fullscreen .image-container {
    max-width: 800px;
    margin: 0 auto;
  }
  
  .landing-page-component.fullscreen .blocks-container:not(.has-fullwidth) {
    max-width: 800px;
    margin: 0 auto;
  }

  /* No width overrides - blocks control their own width via --max-width CSS variable */
  

  /* Center content in fullscreen mode */
  .landing-page-component.fullscreen {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .landing-page-component.fullscreen .content {
    width: 100%;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  /* Text content should also be constrained for readability */
  .landing-page-component.fullscreen .text-content {
    max-width: 680px;
    width: 100%;
    margin: 0 auto;
  }
</style>